import { axios } from "utilities/axios";
import { IInteractionMetric, InteractionMetric, Route } from "@edgetier/watchtower-types";
import { useQuery, UseQueryOptions } from "react-query";
import { AxiosError } from "axios";
import { IMetricDetails } from "./interaction-metrics.types";

/**
 * Request the metrics of an interaction from the backend.
 * @param interactionId The ID of the interaction used to request the metrics.
 * @returns The metrics that belong to the interaction.
 */
export const requestInteractionMetrics = async (interactionId: string) => {
    const { data } = await axios.get<IInteractionMetric>(
        Route.InteractionMetrics.replace(":interactionId", interactionId)
    );
    return data;
};

// Cast to an array of strings so that the type is less narrow and .includes can be run on it with a string.
const metrics = Object.values(InteractionMetric) as string[];

/**
 * Check if a key is a metric.
 * @param key An object key.
 * @returns A boolean indicating if the key is a metric.
 */
const isMetric = (key: string): key is InteractionMetric => metrics.includes(key);

/**
 * Remove all the null values and non-metric parameters from the object, leaving only key-value pairs that are metrics.
 * @param metricObject The metric object returned from the backend. It may have null values which we don't want.
 * @returns A metrics object with only key-value pairs that have non-null values.
 */
export const extractInteractionMetrics = (metric: IInteractionMetric) => {
    return Object.entries(metric).reduce<IMetricDetails[]>((metrics, [key, value]) => {
        if (value !== null && isMetric(key)) {
            return [...metrics, { title: key, value }];
        }
        return metrics;
    }, []);
};

/**
 * A query for requesting the metrics of an interaction.
 * @param interactionId The ID of the interaction used for requesting the metrics.
 * @param configuration An optional react-query configuration.
 */
export const useInteractionMetrics = (
    interactionId: string,
    configuration: UseQueryOptions<IInteractionMetric, AxiosError, IMetricDetails[]> = {}
) => {
    return useQuery<IInteractionMetric, AxiosError, IMetricDetails[]>(
        [Route.InteractionMetrics, interactionId],
        () => requestInteractionMetrics(interactionId),
        {
            ...configuration,
            retry: false,
            select: (metricObject) => extractInteractionMetrics(metricObject),
        }
    );
};
